import { type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

const STORAGE_EVENT_NAME = 'useSafeLocalStorage';

interface StorageEventDetail {
  key: string;
  newValue: string | null;
}

function readStoredValue<T>(storageKey: string, fallback: T): T {
  try {
    const item = window.localStorage.getItem(storageKey);
    if (item === null) {
      return fallback;
    }
    try {
      return JSON.parse(item) as T;
    } catch {
      // The old @rehooks/local-storage stored primitives with String(value)
      // rather than JSON.stringify, so raw strings like "General" are valid.
      return item as unknown as T;
    }
  } catch {
    return fallback;
  }
}

function dispatchStorageChange(key: string, newValue: string | null): void {
  try {
    window.dispatchEvent(
      new CustomEvent<StorageEventDetail>(STORAGE_EVENT_NAME, {
        detail: { key, newValue },
      }),
    );
  } catch {
    // dispatchEvent may fail in restricted environments
  }
}

/**
 * A safe drop-in replacement for `useLocalStorage` from `@rehooks/local-storage`.
 *
 * Unlike the original, this hook catches `QuotaExceededError` (and other
 * storage errors) instead of letting them propagate into the React render
 * cycle, which would cause the nearest error boundary to show an error screen.
 *
 * It synchronises across hook instances sharing the same key via a custom
 * window event (same-window) and the native `storage` event (cross-tab).
 *
 * `defaultValue` is captured by ref so that callers passing object/array
 * literals do not cause unnecessary re-renders or callback churn.
 *
 * API: `const [value, setValue, removeValue] = useSafeLocalStorage<T>(key, defaultValue)`
 */
export function useSafeLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (value: SetStateAction<T>) => void, () => void] {
  const defaultValueRef = useLatestRef(defaultValue);

  const readValue = useCallback((k: string): T => readStoredValue(k, defaultValueRef.current), [defaultValueRef]);

  const [storedValue, setStoredValue] = useState<T>(() => readStoredValue(key, defaultValue));
  const storedValueRef = useLatestRef(storedValue);

  const prevKeyRef = useRef(key);
  useEffect(() => {
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      setStoredValue(readValue(key));
    }
  }, [key, readValue]);

  // Flag to prevent this instance from reacting to its own dispatched events.
  // dispatchEvent is synchronous, so the flag is true only while we're dispatching.
  const isDispatchingRef = useRef(false);

  // Sync with other hook instances (same window) and other tabs.
  useEffect(() => {
    const parseOrDefault = (raw: string | null): T => {
      if (raw === null) {
        return defaultValueRef.current;
      }
      try {
        return JSON.parse(raw) as T;
      } catch {
        return raw as unknown as T;
      }
    };

    const onCustomEvent = (e: Event) => {
      if (isDispatchingRef.current) {
        return;
      }
      const detail = (e as CustomEvent<StorageEventDetail>).detail;
      if (detail.key === key) {
        setStoredValue(parseOrDefault(detail.newValue));
      }
    };

    const onStorageEvent = (e: StorageEvent) => {
      if (e.key === key) {
        setStoredValue(parseOrDefault(e.newValue));
      }
    };

    window.addEventListener(STORAGE_EVENT_NAME, onCustomEvent);
    window.addEventListener('storage', onStorageEvent);
    return () => {
      window.removeEventListener(STORAGE_EVENT_NAME, onCustomEvent);
      window.removeEventListener('storage', onStorageEvent);
    };
  }, [defaultValueRef, key]);

  const setValue = useCallback(
    (value: SetStateAction<T>) => {
      const nextValue = value instanceof Function ? value(storedValueRef.current) : value;
      setStoredValue(nextValue);
      try {
        const serialized = JSON.stringify(nextValue);
        window.localStorage.setItem(key, serialized);
        isDispatchingRef.current = true;
        dispatchStorageChange(key, serialized);
        isDispatchingRef.current = false;
      } catch (e) {
        isDispatchingRef.current = false;
        console.warn(`[useSafeLocalStorage] Failed to set key "${key}":`, e);
      }
    },
    [key, storedValueRef],
  );

  const removeValue = useCallback(() => {
    setStoredValue(defaultValueRef.current);
    try {
      window.localStorage.removeItem(key);
      isDispatchingRef.current = true;
      dispatchStorageChange(key, null);
      isDispatchingRef.current = false;
    } catch (e) {
      isDispatchingRef.current = false;
      console.warn(`[useSafeLocalStorage] Failed to remove key "${key}":`, e);
    }
  }, [defaultValueRef, key]);

  return [storedValue, setValue, removeValue];
}

export default useSafeLocalStorage;
